import {
  formatDate,
  isSameDay,
  centsToDollarString,
} from '@perch-labs/warbler';

export const MAX_DISPLAY_STATEMENTS = 6;

// TODO - add others as they become relevant
export const statementStatuses = {
  issued: 'issued',
  paid: 'paid',
};

export const lineItemKeys = {
  footprint: 'footprint',
  goodEnergy: 'good_energy_donation',
  localUtilityCharge: 'local_utility_charge',
  membership: 'mindful_membership',
  vnmSolarCharge: 'vnm_solar_charge',
};

export const entryTypes = {
  credit: 'credit',
  debit: 'debit',
};

export const THIRTY_DAYS = 30;

// NB: ideally service date would string | undefined, but GQL code gen sets dates to any
export const getServicePeriod = (
  utilityStatement?: {
    serviceStartDate: any;
    serviceEndDate: any;
  } | null
): string | undefined => {
  const serviceStart = utilityStatement?.serviceStartDate;
  const serviceEnd = utilityStatement?.serviceEndDate;
  if (!serviceStart || !serviceEnd) return undefined;
  return `${formatDate(serviceStart, {
    format: 'MMM dd',
  })} - ${formatDate(serviceEnd, { format: 'MMM dd' })}`;
};

interface StatementParam {
  status: string | null;
  paymentScheduledAt: string;
  maxDelayDate: string;
}

export const isIssued = (statement: StatementParam) =>
  statement.status === statementStatuses.issued;
export const allowDelayPayment = (
  statement: StatementParam,
  billPayIsActive: boolean
): boolean => {
  return (
    isIssued(statement) &&
    !isSameDay(statement.maxDelayDate, statement.paymentScheduledAt) &&
    billPayIsActive
  );
};

export const getPercentToSolarFarm = (percentSavings: number): number =>
  100 - percentSavings;

export type StatementFlagParams = {
  paymentTransactions: { disputeId: number | null }[];
  refundedAmountCents: number;
  total: number;
};

export const getStatementFlag = (params: StatementFlagParams) => {
  if (
    params.refundedAmountCents && // non-zero refund
    params.refundedAmountCents === params.total
  ) {
    return 'FULL_REFUND';
  } else if (params.refundedAmountCents) {
    return 'PARTIAL_REFUND';
  } else if (params.paymentTransactions.some(t => t.disputeId)) {
    return 'DISPUTED';
  } else {
    return 'NONE';
  }
};

export const getStatementFlagCopy = <
  TGetCopy extends (
    field: 'refundedFull' | 'refundedPartial' | 'disputed',
    args?: any
  ) => any
>({
  statement,
  getCopy,
}: {
  statement: StatementFlagParams;
  getCopy: TGetCopy;
}): string | undefined => {
  const flag = getStatementFlag(statement);
  if (flag === 'FULL_REFUND') {
    return getCopy('refundedFull');
  } else if (flag === 'PARTIAL_REFUND') {
    return getCopy('refundedPartial', {
      dollarAmount: centsToDollarString(statement.refundedAmountCents),
    });
  } else if (flag === 'DISPUTED') {
    return getCopy('disputed');
  } else if (flag === 'NONE') {
    return undefined;
  } else {
    return undefined as never;
  }
};
